import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTopButton from '@/components/base/ScrollToTopButton';
import { getMovieById, getRelatedMovies, type Movie } from '@/mocks/movies';

export default function Movie() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id || '');

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h1 className="mt-4 text-xl font-semibold text-foreground-50">电影未找到</h1>
            <p className="mt-2 text-foreground-400">该电影不存在或已下架</p>
            <Link
              to="/"
              className="inline-block mt-6 px-6 py-2.5 bg-primary-500 text-foreground-50 font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMovies = getRelatedMovies(movie, 6);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Movie',
        name: movie.title,
        description: movie.description,
        image: movie.posterUrl,
        dateCreated: movie.releaseDate,
        director: { '@type': 'Person', name: movie.director },
        actor: movie.actors.map((a) => ({ '@type': 'Person', name: a })),
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: movie.rating,
          bestRating: 10,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.97cinema.com/' },
          { '@type': 'ListItem', position: 2, name: movie.category, item: `https://www.97cinema.com/category/${movie.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: movie.title },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${movie.title} - ${movie.category}高清在线观看 | 97影院`}
        description={`${movie.title}（${movie.year}）${movie.description.substring(0, 80)}... 导演：${movie.director}，主演：${movie.actors.join('、')}。${movie.category}高清蓝光免费在线观看。`}
        keywords={`${movie.title},${movie.category},${movie.director},${movie.actors.slice(0, 3).join(',')},在线观看,97影院,高清电影`}
        canonical={`https://www.97cinema.com/movie/${movie.id}`}
        ogTitle={`${movie.title} - 97影院高清在线观看`}
        ogDescription={movie.description}
        ogImage={movie.posterUrl}
        ogType="video.movie"
      />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: movie.category, href: `/category/${movie.categorySlug}` },
                  { label: movie.title },
                ]}
              />

              <article className="mt-6">
                <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                  <div className="flex-shrink-0">
                    <div className="w-40 md:w-56 aspect-[2/3] rounded-lg overflow-hidden">
                      <img
                        src={movie.posterUrl}
                        alt={movie.title}
                        title={`${movie.title} - ${movie.category}高清在线观看 - 97影院`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  <div className="flex-1">
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50">
                      {movie.title}
                    </h1>
                    <p className="mt-2 text-sm text-foreground-400">{movie.originalTitle}</p>

                    <div className="flex items-center gap-3 mt-4 flex-wrap">
                      <span className="px-3 py-1.5 bg-primary-500/10 text-primary-500 text-sm font-heading font-bold rounded-lg whitespace-nowrap">
                        {movie.rating} 分
                      </span>
                      <span className="text-sm text-foreground-300">{movie.year}</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-foreground-500"></span>
                      <span className="text-sm text-foreground-300">{movie.duration}</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-foreground-500"></span>
                      <span className="text-sm text-foreground-300">{movie.region}</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-foreground-500"></span>
                      <Link
                        to={`/category/${movie.categorySlug}`}
                        className="text-sm text-primary-500 hover:text-primary-400 transition-colors cursor-pointer"
                      >
                        {movie.category}
                      </Link>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      {movie.tags.map((tag) => (
                        <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-md whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-foreground-400">导演：</span>
                        <span className="text-foreground-200">{movie.director}</span>
                      </div>
                      <div>
                        <span className="text-foreground-400">主演：</span>
                        <span className="text-foreground-200">{movie.actors.join(' / ')}</span>
                      </div>
                      <div>
                        <span className="text-foreground-400">上映日期：</span>
                        <span className="text-foreground-200">{movie.releaseDate}</span>
                      </div>
                      <div>
                        <span className="text-foreground-400">地区：</span>
                        <span className="text-foreground-200">{movie.region}</span>
                      </div>
                    </div>

                    <div className="mt-6">
                      <Link
                        to={`/category/${movie.categorySlug}`}
                        className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-foreground-50 font-semibold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-play-circle-line text-lg"></i>立即观看
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="mt-8 bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
                  <h2 className="text-lg font-heading font-bold text-foreground-50 mb-4">剧情简介</h2>
                  <div
                    className="text-sm text-foreground-300 leading-relaxed space-y-3"
                    dangerouslySetInnerHTML={{ __html: movie.descriptionHtml }}
                  />
                </div>

                {relatedMovies.length > 0 && (
                  <div className="mt-10">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-heading font-bold text-foreground-50">
                        相关<span className="text-primary-500">推荐</span>
                      </h2>
                      <Link
                        to={`/category/${movie.categorySlug}`}
                        className="text-sm text-primary-500 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        查看更多 <i className="ri-arrow-right-line ml-1"></i>
                      </Link>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                      {relatedMovies.map((rm) => (
                        <MovieMiniCard key={rm.id} movie={rm} />
                      ))}
                    </div>
                  </div>
                )}
              </article>
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTopButton />
      </div>
    </>
  );
}

function MovieMiniCard({ movie }: { movie: Movie }) {
  return (
    <Link
      to={`/movie/${movie.id}`}
      className="group block bg-background-100 rounded-lg overflow-hidden border border-background-200 hover:border-primary-500/50 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[2/3] overflow-hidden">
        <img
          src={movie.posterUrl}
          alt={movie.title}
          title={`${movie.title} - 97影院高清在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
        <div className="absolute top-2 right-2">
          <span className="px-1.5 py-0.5 bg-background-950/70 text-accent-500 text-xs font-heading font-bold rounded whitespace-nowrap">
            {movie.rating}
          </span>
        </div>
        <div className="absolute bottom-2 left-2 right-2">
          <h3 className="text-xs font-semibold text-foreground-50 line-clamp-1">{movie.title}</h3>
        </div>
      </div>
    </Link>
  );
}